import React, { useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { useDispatch } from "react-redux";

//Custom Imports
import Edit from "@assets/svg/Edit";
import color from "@constants/color";
import t from "@constants/localization";
import Text from "@components/CustomText";
import { width } from "@constants/metrics";
import ToastMessage from "@components/Toast";
import { deleteVacation } from "@services/api";
import Info from "@assets/svg/MyDeliveries/Info";
import vacationActions from "@actions/vacation.actions";
import DottedLine from "@assets/svg/Contest/DottedLine";
import { getHardDayMonth } from "@utils/getFormattedDate";
import BottomSheet from "@screens/Vacation/components/BottomSheet";

const PATH = "screens/Vacation/index";

export const UpcomingDates = ({
  textColor = "",
  date = "",
  backgroundColor = "",
  borderColor = "",
  style = {},
  isEdit = true,
  onPressEditLeave = () => {},
  item = {},
}) => {
  return (
    <Pressable
      style={[
        styles.datesContainer,
        style,
        {
          backgroundColor,
          borderColor,
        },
      ]}
      disabled={!isEdit}
      onPress={() => onPressEditLeave(item)}
    >
      <Text
        variant="body_text_3"
        newfont={true}
        style={{
          color: textColor,
        }}
      >
        {date}
      </Text>
      {isEdit && <Edit style={styles.editIcon} width="12" height="12" />}
    </Pressable>
  );
};

export const LeaveInfo = ({
  textColor = "",
  info = "",
  infoColor,
  style = {},
}) => {
  return (
    <View style={[styles.infoTextContainer, style]}>
      <Info style={styles.infoIcon} color={infoColor} />
      <Text
        variant="body_text_3"
        newfont={true}
        style={{
          color: textColor,
          marginStart: 6,
        }}
      >
        {info}
      </Text>
    </View>
  );
};

const UpcominngLeave = ({
  upcoming_cancelled_leaves = [],
  upcoming_leaves = [],
  navigation,
  labels = {},
  hideInfo = () => {},
}) => {
  const refRBSheet = useRef();
  const dispatch = useDispatch();
  const [leaveData, setLeaveData] = useState({});

  const onPressEditLeave = (item) => {
    setLeaveData(item);
    hideInfo();
    refRBSheet.current.open();
  };

  const onEditLeave = ({ vacation_date, type, status }) => {
    navigation &&
      navigation.navigate("VacationCalendar", {
        isEdit: true,
        vacation_date,
        type,
      });
  };

  const onDeleteLeave = async ({ vacation_date }) => {
    try {
      const { data } = await deleteVacation({
        vacation_dates: [vacation_date],
      });
      data?.toastMessage && ToastMessage("info", data.toastMessage);
      dispatch(vacationActions.getVacationDetails.pending());
    } catch (error) {
      console.log(error);
    }
  };

  const renderDates = (item, index) =>
    item.type === "PAID" ? (
      <UpcomingDates
        backgroundColor={"rgba(0, 171, 89, 0.05)"}
        textColor={color.plain_green}
        date={getHardDayMonth(item.vacation_date)}
        borderColor={"rgba(0, 171, 89, 0.2)"}
        onPressEditLeave={onPressEditLeave}
        item={item}
        style={styles.unpaidLeave}
        isEdit={item?.editable}
        key={`${index}${item?.vacation_date}`}
      />
    ) : (
      <UpcomingDates
        backgroundColor={"rgba(255, 122, 0, 0.05)"}
        textColor={"#FF7A00"}
        date={getHardDayMonth(item.vacation_date)}
        borderColor={"rgba(255, 122, 0, 0.2)"}
        style={styles.unpaidLeave}
        onPressEditLeave={onPressEditLeave}
        item={item}
        isEdit={item?.editable}
        key={`${index}${item?.vacation_date}`}
      />
    );

  return (
    <View style={styles.container}>
      <Text
        style={{ marginHorizontal: 16 }}
        variant="subtitle_16"
        newfont={true}
      >
        {t(`${PATH}/upcomingLeaves`)}
      </Text>
      {upcoming_leaves.length !== 0 && (
        <React.Fragment>
          <View style={styles.showDateContainer}>
            {upcoming_leaves.map(renderDates)}
          </View>
          <LeaveInfo
            textColor={color.lightBlack}
            info={labels?.delete_edit_text}
            infoColor={color.lightMediumBlack}
            style={styles.leaveInfo}
          />
        </React.Fragment>
      )}
      {upcoming_cancelled_leaves.length !== 0 && (
        <React.Fragment>
          {upcoming_leaves.length !== 0 && (
            <DottedLine style={styles.dottedLineStyle} width={width - 32} />
          )}
          {upcoming_cancelled_leaves.map((item, index) => (
            <React.Fragment key={item.vacation_date}>
              <View style={[styles.cancelledLeave]}>
                <UpcomingDates
                  backgroundColor={"rgba(255, 46, 0, 0.05)"}
                  textColor={color.plain_red}
                  date={getHardDayMonth(item.vacation_date)}
                  borderColor={"rgba(255, 46, 0, 0.2)"}
                  isEdit={false}
                  key={`${index}${item?.vacation_date}`}
                />
              </View>
              <LeaveInfo
                textColor={color.plain_red}
                info={item.deleted_reason}
                infoColor={color.plain_red}
                style={styles.leaveInfo}
              />
            </React.Fragment>
          ))}
        </React.Fragment>
      )}

      <BottomSheet
        onEdit={onEditLeave}
        onDelete={onDeleteLeave}
        leaveData={leaveData}
        setLeaveData={setLeaveData}
        ref={refRBSheet}
      />
    </View>
  );
};

export default UpcominngLeave;

const styles = StyleSheet.create({
  container: {
    marginTop: 40,
    backgroundColor: color.white,
  },
  mainDateContainer: {
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
  },
  datesContainer: {
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 40,
    backgroundColor: color.plain_green,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    marginStart: 10,
  },
  unpaidLeave: {
    marginStart: 12,
    marginTop: 12,
  },
  infoTextContainer: {
    flexDirection: "row",
    marginTop: 16,
  },
  dottedLineStyle: {
    marginTop: 16,
    alignSelf: "center",
  },
  cancelledLeave: {
    flexDirection: "row",
    marginHorizontal: 16,
    marginTop: 16,
  },
  infoIcon: {
    marginTop: 4,
  },
  bottomSheet: {
    backgroundColor: "white",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    flex: 1,
    paddingHorizontal: 24,
  },
  header: {
    paddingVertical: 20,
    justifyContent: "space-between",
    flexDirection: "row",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "bold",
  },
  showDateContainer: {
    lineHeight: 44,
    backgroundColor: color.white,
    flexGrow: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginStart: 4,
    marginEnd: 16,
  },
  leaveInfo: {
    marginHorizontal: 16,
  },
});
